import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  Link,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Login,
  Phone,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import httpClient from 'src/libs/axios';
import { API_ROUTES } from 'src/constants/routes';
import { PhoneVerification } from 'src/components/PhoneVerification';
import { useAppDispatch } from 'src/store';
import { loginWithSms } from 'src/features/auth/auth.slice';
import { trackAnalytics } from 'src/features/analytics/analytics.slice';

interface SignInFormProps {
  onToggleRegister?: () => void;
  onSuccess?: () => void;
}

const SignInForm: React.FC<SignInFormProps> = ({ onToggleRegister, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signInMode, setSignInMode] = useState<'email' | 'phone'>('email');

  const handlePhoneLogin = async (phoneNumber: string, token: string) => {
    setLoading(true);
    try {
      await dispatch(loginWithSms({ phone_token: token })).unwrap();
      dispatch(trackAnalytics({ 
        action_name: "user_logged_in", 
        payload: { description: `user logged in with phone number ${phoneNumber}` } 
      }));
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/conversations');
      }
    } catch (error) {
      setError('Phone sign-in failed. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await httpClient.post(API_ROUTES.login, {
        user: {
          email,
          password,
        }
      });

      if (response.data.access_token) {
        localStorage.setItem('user', JSON.stringify(response.data));
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/conversations');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 0,
        borderRadius: 3,
        background: theme.palette.background.paper,
        position: 'relative',
        overflow: 'visible',
        width: '100%',
        minHeight: 'auto',
      }}
    >
      <Box sx={{ p: isMobile ? 3 : 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700, 
            mb: 1, 
            color: theme.palette.text.primary,
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
          }}>
            Sign In
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Don't have an account?{' '}
            <Link
              component="button"
              variant="body1"
              onClick={onToggleRegister}
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Create Account
            </Link>
          </Typography>
        </Box>

        {/* Sign-in Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <ToggleButtonGroup
            value={signInMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode !== null) {
                setSignInMode(newMode);
                setError('');
              }
            }}
            aria-label="sign in mode"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 3,
                py: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <ToggleButton value="email" aria-label="email sign in">
              <Email sx={{ mr: 1, fontSize: 18 }} />
              Email
            </ToggleButton>
            <ToggleButton value="phone" aria-label="phone sign in">
              <Phone sx={{ mr: 1, fontSize: 18 }} />
              Phone
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {signInMode === 'email' ? (
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
            <TextField
              fullWidth
              size="small"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email Address"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: theme.palette.action.active, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              size="small"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label="Password"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: theme.palette.action.active, fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Typography color="error" variant="caption" sx={{ mt: -1 }}>
                {error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -1 }}>
              <Link
                href="/forgot-password"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  textDecoration: 'none',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              endIcon={<Login />}
              sx={{
                mt: 2,
                py: { xs: 1.25, md: 1.5 },
                borderRadius: 2,
                textTransform: 'none',
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                },
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                By signing in, you agree to our{' '}
                <Link
                  href="https://oppy.ai/terms"
                  target="_blank"
                  sx={{ color: theme.palette.primary.main }}
                >
                  Terms of Service
                </Link>
              </Typography>
            </Box>
          </Box>
        </form>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PhoneVerification
              onPhoneVerification={(phoneNumber, token, isExistingUser) => {
                handlePhoneLogin(phoneNumber, token);
              }}
              existingUser={true}
            />
            {error && (
              <Typography color="error" variant="caption" sx={{ textAlign: 'center', mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SignInForm;